import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Users, UserPlus, UserCheck, Trash2, Mail, Phone } from "lucide-react";

const empty = { name: "", email: "", phone: "", password: "cliente123" };

export default function Clients() {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");

  const load = () => api.get("/clients").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/clients", form);
      toast.success("Cliente cadastrado");
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const linkSave = async () => {
    try {
      await api.post("/clients/link", { email: linkEmail });
      toast.success("Cliente vinculado a você");
      setLinkOpen(false); setLinkEmail(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/clients/${id}`); toast.success("Cliente removido"); load(); };

  return (
    <div>
      <PageHeader title="Clientes" subtitle="Seus clientes vinculados"
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={() => { setLinkEmail(""); setLinkOpen(true); }} className="rounded-full" data-testid="link-client-btn"><UserCheck className="w-4 h-4 mr-1" />Vincular existente</Button>
          <Button onClick={() => setOpen(true)} className="rounded-full" data-testid="add-client-btn"><UserPlus className="w-4 h-4 mr-1" />Adicionar cliente</Button>
        </div>} />
      {!list ? <div className="h-40 animate-pulse bg-secondary rounded-2xl" /> :
        list.length === 0 ? <EmptyState icon={Users} title="Nenhum cliente" desc="Cadastre seu primeiro cliente." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((c) => {
            const total = c.pending_exercises + c.completed_exercises;
            const pct = total ? Math.round((c.completed_exercises / total) * 100) : 0;
            return (
              <Card key={c.id} className="p-5" data-testid={`client-card-${c.id}`}>
                <div className="flex items-center gap-3">
                  <Avatar className="w-11 h-11"><AvatarFallback className="bg-primary/10 text-primary">{c.name[0]}</AvatarFallback></Avatar>
                  <div className="min-w-0 flex-1"><div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground truncate flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</div></div>
                </div>
                {c.phone && <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</div>}
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>Progresso dos exercícios</span><span>{pct}%</span></div>
                  <Progress value={pct} className="h-2" />
                  <div className="text-xs text-muted-foreground mt-1">{c.pending_exercises} pendente(s) · {c.completed_exercises} concluído(s)</div>
                </div>
                <Button variant="outline" size="sm" className="text-destructive mt-4 w-full" onClick={() => del(c.id)}><Trash2 className="w-4 h-4 mr-1" />Remover</Button>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Adicionar cliente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="client-name-input" className="mt-1" /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email-input" className="mt-1" /></div>
            <div><Label>Telefone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1" /></div>
            <div><Label>Senha de acesso</Label><Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" />
              <p className="text-xs text-muted-foreground mt-1">O cliente usará este e-mail e senha para acessar.</p></div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="save-client-btn">Cadastrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vincular cliente existente</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Informe o e-mail de um cliente que já possui conta no Teraviva para vinculá-lo a você.</p>
            <div><Label>E-mail do cliente</Label><Input type="email" value={linkEmail} onChange={(e) => setLinkEmail(e.target.value)} data-testid="link-client-email-input" className="mt-1" placeholder="cliente@email.com" /></div>
          </div>
          <DialogFooter><Button onClick={linkSave} data-testid="confirm-link-client-btn"><UserCheck className="w-4 h-4 mr-1" />Vincular cliente</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
